/**
 * Raised when re-invoicing ran but the PAC rejected one or more CFDIs.
 *
 * `reinvoice-sync` answers 200 whether it stamped or not — the outcome lives per invoice in the
 * body. This error is what turns that into a rejection, so the caller's failure path is the same
 * one it already uses for a request that never went through.
 *
 * `reasons` are the backend's raw messages, one per rejected CFDI, left untranslated: the
 * presentation layer owns the SAT/PAC vocabulary mapping.
 *
 * Those messages carry PII — the PAC quotes the taxpayer's full RFC back in several of them
 * ("El regimen 601 no aplica para persona el rfc: …"). They are therefore kept **out of
 * `message`**, which is what generic error handling logs: `message` stays a safe summary and the
 * reasons travel in a field only the UI reads. See Charter §5.1.
 */
export type RetryInvoiceError = Error & {
  readonly name: 'RetryInvoiceError';
  readonly reasons: string[];
};

/**
 * Creates a RetryInvoiceError from the rejected invoices' messages.
 *
 * @param reasons - Backend messages of every CFDI that came back FAILED. Never interpolated into
 * the error message: they may quote a full RFC.
 * @returns A RetryInvoiceError instance (also an Error at runtime)
 */
export const createRetryInvoiceError = (reasons: string[]): RetryInvoiceError => {
  const err = new Error(`Invoice re-issuing rejected for ${reasons.length} CFDI`) as RetryInvoiceError;
  return Object.assign(err, { name: 'RetryInvoiceError' as const, reasons });
};

/**
 * Type guard narrowing an unknown value to RetryInvoiceError.
 * Prefer this over `instanceof` since the error is a branded Error, not a class.
 */
export const isRetryInvoiceError = (error: unknown): error is RetryInvoiceError =>
  error instanceof Error && (error as RetryInvoiceError).name === 'RetryInvoiceError';
